import { createSelector } from "reselect";
import { getCurrentMock } from "./mockSelectors";
import { getQuestionStems } from "./questionStemSelectors";

const getQuestions = (state) => state.entities.questions;
const getCurrentIndex = (state) => state.mock.currentIndex;
const getIndex = (_, index) => index;
const getQuestionFetchedIds = (state) => state.questionList.questionIds;

export const getAllQuestions = createSelector(getQuestions, (questions) =>
  Object.values(questions)
);

export const getQuestionById = createSelector(
  getQuestions,
  getIndex,
  (questions, id) => questions[id]
);

export const getQuestionsFromMock = createSelector(
  getQuestions,
  getCurrentMock,
  (questions, mock) =>
    mock?.questionIds
      ?.map((questionId) => questions[questionId])
      .filter((q) => q) ?? []
);

export const getQuestionByIndex = createSelector(
  getQuestionsFromMock,
  getIndex,
  (questions, index) => Object.values(questions)[index]
);

export const getQuestionsByFetchedIds = createSelector(
  getAllQuestions,
  getQuestionFetchedIds,
  (questions, ids) => questions.filter((question) => ids.includes(question.id))
);


const calculateDisplayIndex = (questionIndex) => {
  return String(questionIndex + 1);
};

/**
 * getFlattenedQuestionsFromMock
 *
 * For each question, produce pagination entries:
 * - v1 or v2 multiple_choice with no stems: 1 entry (stem: null, isFlow: false)
 * - v2 case with N stems: N entries, one per stem
 * - interactive (v2) WITHOUT stems: 1 entry (isFlow: true) — the flow as a whole is 1 pagination unit
 * - interactive (v2) WITH stems (hybrid): 1 flow entry (isFlow: true) + N stem entries
 *
 * The interactive flow is a single pagination unit because it is scored as one
 * binary unit over the full flow — proportional points with a 70% pass ratio
 * (MockScoreCalculator / V2QuestionUnits).
 */
export const getFlattenedQuestionsFromMock = createSelector(
  getQuestionsFromMock,
  getQuestionStems,
  (questions, allStems) => {
    const flattened = [];
    let displayQuestionIndex = 0;

    const getStemsForQuestion = (question) => {
      return Object.values(allStems).filter((stem) => {
        const stemQuestionId = stem.questionId || stem.question_id;
        return stemQuestionId != null && Number(stemQuestionId) === Number(question.id);
      });
    };

    questions.forEach((question) => {
      const isInteractive = question?.format === "interactive";

      if (isInteractive) {
        // 1 flow entry for the whole interactive flow
        flattened.push({
          question,
          stem: null,
          isFlow: true,
          questionIndex: displayQuestionIndex,
          stemPosition: null,
          displayIndex: calculateDisplayIndex(displayQuestionIndex),
        });
        displayQuestionIndex++;

        // + 1 entry per stem if the interactive question has stems (hybrid)
        const stems = getStemsForQuestion(question).sort((a, b) => a.position - b.position);
        stems.forEach((stem) => {
          flattened.push({
            question,
            stem,
            isFlow: false,
            questionIndex: displayQuestionIndex,
            stemPosition: stem.position,
            displayIndex: calculateDisplayIndex(displayQuestionIndex),
          });
          displayQuestionIndex++;
        });
        return;
      }

      let version = question?.version;

      if (!version) {
        const stemsForQuestion = getStemsForQuestion(question);
        version = stemsForQuestion.length > 0 ? 2 : 1;
      }

      if (version === 2) {
        const stems = getStemsForQuestion(question).sort((a, b) => a.position - b.position);

        if (stems.length === 0) {
          flattened.push({
            question,
            stem: null,
            isFlow: false,
            questionIndex: displayQuestionIndex,
            stemPosition: null,
            displayIndex: calculateDisplayIndex(displayQuestionIndex),
          });
          displayQuestionIndex++;
        } else {
          stems.forEach((stem) => {
            flattened.push({
              question,
              stem,
              isFlow: false,
              questionIndex: displayQuestionIndex,
              stemPosition: stem.position,
              displayIndex: calculateDisplayIndex(displayQuestionIndex),
            });
            displayQuestionIndex++;
          });
        }
      } else {
        flattened.push({
          question,
          stem: null,
          isFlow: false,
          questionIndex: displayQuestionIndex,
          stemPosition: null,
          displayIndex: calculateDisplayIndex(displayQuestionIndex),
        });
        displayQuestionIndex++;
      }
    });

    return flattened;
  }
);

export const getCurrentQuestionAndStem = createSelector(
  getFlattenedQuestionsFromMock,
  getCurrentIndex,
  (flattenedQuestions, currentIndex) => {
    const current = flattenedQuestions[currentIndex];
    if (!current) {
      return { question: null, stem: null, isFlow: false, displayIndex: null };
    }

    return {
      question: current.question,
      stem: current.stem,
      isFlow: !!current.isFlow,
      questionIndex: current.questionIndex,
      stemPosition: current.stemPosition,
      displayIndex: current.displayIndex,
    };
  }
);

export const makeCurrentQuestion = createSelector(
  getCurrentQuestionAndStem,
  (current) => current.question
);
